import React from 'react';
import ReactECharts from 'echarts-for-react';
import { BOOTSTRAP_COLORS } from '@/lomavisSharedUi/colors';
import { labelStyle } from '../chartTextStyles';

// import { translateComponent } from '@/utils/tolgee/translateComponent';

interface LomavisBarChart3Props {
  data: BarChartData3[];
  zoomControl?: boolean;
  style?: React.CSSProperties;
  showAllXAxisLabels?: boolean; // New prop to control x-axis label display
}

export interface BarChartData3 {
  label: string;
  data: Record<string, number | null | undefined>;
}

/**
 * Atoms: LomavisBarChart3
 */
export const LomavisBarChart3: React.FC<LomavisBarChart3Props> = ({
  data,
  zoomControl = true,
  style,
  showAllXAxisLabels = false // Default to false to maintain existing behavior
}) => {
  //   const t = translateComponent('LomavisBarChart2');

  const days = data.map((d) => d.label);
  // Collect unique keys from all data entries
  const allKeys = Array.from(new Set(data.flatMap((d) => Object.keys(d.data))));

  const seriesList = allKeys.map((key, idx) => ({
    name: key,
    data: data.map((d) => d.data[key] ?? 0)
  }));

  // --- Calculate zoom range ---
  let startPercent = 0;
  let endPercent = 100;

  if (zoomControl && days.length > 14) {
    // Find the last index where there is actual data
    const lastDataIndex = data
      .map((d, idx) => ({
        idx,
        hasData: Object.values(d.data).some((val) => val != null && val !== 0)
      }))
      .filter((d) => d.hasData)
      .map((d) => d.idx)
      .pop();

    if (lastDataIndex !== undefined) {
      let startIndex = lastDataIndex - 13;
      let endIndex = lastDataIndex;

      // If not enough days before, extend after the last data day
      if (startIndex < 0) {
        endIndex = Math.min(days.length - 1, endIndex + Math.abs(startIndex));
        startIndex = 0;
      }

      // Ensure exactly 14 days window if possible
      if (endIndex - startIndex < 13) {
        startIndex = Math.max(0, endIndex - 13);
      }

      startPercent = (startIndex / (days.length - 1)) * 100;
      endPercent = (endIndex / (days.length - 1)) * 100;
    }
  }

  // Configure x-axis label settings based on the showAllXAxisLabels prop
  const xAxisLabelConfig = showAllXAxisLabels
    ? {
        ...labelStyle,
        interval: 0, // Show all labels
        rotate: days.length > 12 ? 45 : 0, // Rotate labels if there are many
        fontSize: Math.max(8, Math.min(12, 120 / days.length)), // Dynamic font size based on number of labels
        overflow: 'break' as const,
        width: Math.max(30, 600 / days.length) // Dynamic width based on available space
      }
    : labelStyle;

  const option: echarts.EChartsOption = {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' }
    },
    legend: { data: allKeys },
    grid: {
      left: '3%',
      right: '4%',
      bottom: showAllXAxisLabels && days.length > 10 ? 100 : 80, // More bottom space when labels are rotated
      containLabel: true
    },
    dataZoom: zoomControl
      ? [
          {
            type: 'slider',
            show: true,
            xAxisIndex: 0,
            start: startPercent,
            end: endPercent
          }
        ]
      : [],
    xAxis: {
      type: 'category',
      data: days,
      axisLabel: xAxisLabelConfig,
      splitArea:
        showAllXAxisLabels && days?.length > 1
          ? {
              show: true
            }
          : {
              show: false
            }
    },
    yAxis: {
      type: 'value'
    },
    series: seriesList.map((s, index) => ({
      name: s.name,
      type: 'bar',
      data: s.data,
      itemStyle: {
        borderRadius: [4, 4, 0, 0],
        color: BOOTSTRAP_COLORS[index]
      },
      emphasis: { focus: 'series' }
    }))
  };

  return (
    <div style={{ height: '100%', width: '100%', ...style }}>
      <ReactECharts option={option} style={{ height: '100%', width: '100%' }} />
    </div>
  );
};
